import { useState, useEffect, type KeyboardEvent } from 'react';
import { Box, IconButton, Typography, Avatar, Paper } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  organization?: string;
  organizationLogo?: string;
  image?: string;
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  autoPlay?: boolean;
  interval?: number;
  showLogos?: boolean;
}

export function TestimonialCarousel({
  testimonials,
  autoPlay = true,
  interval = 6000,
  showLogos = false,
}: TestimonialCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoPlay || isPaused) return;

    const timer = setInterval(() => {
      handleNext();
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, currentIndex, isPaused]);

  const goToIndex = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <Box
      sx={{ position: 'relative', maxWidth: 800, mx: 'auto' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      {/* Main testimonial card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 6 },
          borderRadius: 4,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.06)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          textAlign: 'center',
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          key={currentIndex}
          aria-live="polite"
          aria-atomic="true"
          sx={{
            animation: 'fadeIn 0.5s ease-out',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateY(10px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <FormatQuoteIcon
            sx={{
              fontSize: 48,
              color: 'primary.main',
              opacity: 0.2,
              mb: 2,
            }}
          />

          <Typography
            variant="h5"
            component="blockquote"
            sx={{
              fontStyle: 'italic',
              color: 'text.primary',
              mb: 4,
              lineHeight: 1.8,
              fontWeight: 500,
              fontSize: { xs: '1.125rem', md: '1.375rem' },
            }}
          >
            "{currentTestimonial.quote}"
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Avatar
              src={currentTestimonial.image}
              alt={currentTestimonial.name}
              sx={{
                bgcolor: 'primary.main',
                width: 64,
                height: 64,
                boxShadow: '0 4px 16px rgba(77, 101, 255, 0.3)',
              }}
            >
              {currentTestimonial.name.charAt(0)}
            </Avatar>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" component="p" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                {currentTestimonial.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {currentTestimonial.role}
                  {currentTestimonial.organization && !showLogos && `, ${currentTestimonial.organization}`}
                </Typography>

                {showLogos && currentTestimonial.organizationLogo && (
                  <Box
                    component="img"
                    src={currentTestimonial.organizationLogo}
                    alt={currentTestimonial.organization || 'Organization Logo'}
                    sx={{
                      height: 16,
                      width: 'auto',
                      opacity: 0.6,
                      filter: 'grayscale(100%)',
                      ml: 1
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Navigation arrows */}
      <IconButton
        onClick={handlePrev}
        aria-label="Previous testimonial"
        sx={{
          position: 'absolute',
          left: { xs: -8, md: -24 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            bgcolor: 'background.paper',
            borderColor: 'primary.main',
          },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      <IconButton
        onClick={handleNext}
        aria-label="Next testimonial"
        sx={{
          position: 'absolute',
          right: { xs: -8, md: -24 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            bgcolor: 'background.paper',
            borderColor: 'primary.main',
          },
        }}
      >
        <ChevronRightIcon />
      </IconButton>

      {/* Dots indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3 }}>
        {testimonials.map((_, index) => (
          <Box
            key={index}
            role="button"
            tabIndex={0}
            aria-label={`Go to testimonial ${index + 1} of ${testimonials.length}`}
            aria-current={index === currentIndex ? 'true' : undefined}
            onClick={() => goToIndex(index)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToIndex(index);
              }
            }}
            sx={{
              // WCAG 2.2 target-size (2.5.8): >=24x24px hit area with adequate spacing.
              // The visual dot stays 8px; this wrapper provides the accessible target.
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 24,
              p: 0,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              borderRadius: 2,
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
              '&:hover > span': {
                bgcolor: index === currentIndex ? 'primary.main' : 'grey.400',
              },
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'block',
                width: index === currentIndex ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: index === currentIndex ? 'primary.main' : 'grey.300',
                transition: 'all 0.3s ease',
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
